import Select from "react-select";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import { Button, Form } from "react-bootstrap";
import React, { useEffect, useState } from "react";
import { useLoader } from "../../_helper/LoaderContext";
import { useLab } from "../../_helper/PharmacyProvider";
import { formatDoctorDisplayDateTime } from "../../utils/dateTime/dateFormat";
import { masterEquipment } from "../ManageEquipment/service/equipment-service";
import { useTranslation } from "react-i18next";
import { SAMPLE_COLLECTION_UPDATE_STATUSES, updateSampleCollection, updateSampleCollectionStatus, viewSampleCollection, } from "./service/sample-collection-service";

const selectStyles = {
    control: (provided) => ({
        ...provided,
        minHeight: "38px",
    }),
    multiValue: (styles) => ({
        ...styles,
        backgroundColor: "#1b255b",
    }),
    multiValueLabel: (styles) => ({
        ...styles,
        color: "#fff",
        fontWeight: 500,
    }),
    multiValueRemove: (styles) => ({
        ...styles,
        color: "#d32f2f",
        ":hover": {
            backgroundColor: "#ffcdd2",
            color: "white",
        },
    }),
};

const toEquipmentOptions = (list = []) =>
    (Array.isArray(list) ? list : [])
        .map((item) => {
            if (!item) return null;
            if (typeof item === "string") {
                return { value: item, label: item };
            }
            const eqId = item._id || item.value;
            if (!eqId) return null;
            return {
                value: eqId,
                label: item.code ? `${item.name} (${item.code})` : item.name || eqId,
            };
        })
        .filter(Boolean);

export default function SampleDetailModal({
    show,
    onHide,
    sampleId,
    initialData = null,
    mode = "view",
    onUpdated,
}) {
    const { t } = useTranslation();
    const { setLoading } = useLoader();
    const { currentLab } = useLab();
    const formatDateTime = (value) => formatDoctorDisplayDateTime(value, currentLab) || "—";
    const [detail, setDetail] = useState(null);
    const [notes, setNotes] = useState("");
    const [status, setStatus] = useState("");
    const [equipmentOptions, setEquipmentOptions] = useState([]);
    const [selectedEquipment, setSelectedEquipment] = useState([]);
    const [editable, setEditable] = useState(mode === "edit");

    const isBarcodeMode = mode === "barcode";

    useEffect(() => {
        if (!show) return;
        setEditable(mode === "edit");
        loadData();        
    }, [show, sampleId, mode]);

    const applyDetail = (data) => {
        setDetail(data);
        setNotes(data?.notes || "");
        setStatus(data?.status || "");
        setSelectedEquipment(toEquipmentOptions(data?.assignedEquipment || []));
    };

    const loadData = async () => {
        if (initialData && (isBarcodeMode || !sampleId)) {
            applyDetail(initialData);
            if (mode === "edit" || mode === "view") {
                fetchEquipmentMaster(toEquipmentOptions(initialData?.assignedEquipment || []));
            }
            return;
        }

        if (!sampleId) return;

        setLoading(true);
        try {
            const res = await viewSampleCollection(sampleId);
            if (res.data.success === true) {
                applyDetail(res.data.data);
                await fetchEquipmentMaster(toEquipmentOptions(res.data.data?.assignedEquipment || []));
            } else {
                toast.warning(res.data.message || t("Unable to load sample details"));
            }
        } catch (error) {
            console.log(error);
            toast.warning(t("Unable to load sample details"));
        } finally {
            setLoading(false);
        }
    };

    const fetchEquipmentMaster = async (selected = []) => {
        try {
            const res = await masterEquipment();
            if (res.data.success === true) {
                const options = toEquipmentOptions(res.data.data || []);
                setEquipmentOptions(options);
                setSelectedEquipment((prev) => {
                    const current = selected.length ? selected : prev;
                    return current.map((sel) => {
                        const match = options.find((opt) => opt.value === sel.value);
                        return match || sel;
                    });
                });
            }
        } catch (error) {
            console.log(error);
        }
    };

    const handleSave = async () => {
        if (!detail?._id) return;

        const payload = {
            notes: notes || "",
            assignedEquipment: selectedEquipment.map((item) => item.value),
        };
        if (status && status !== detail.status) {
            payload.status = status;
        }

        setLoading(true);
        try {
            const res = await updateSampleCollection(detail._id, payload);
            if (res.data.success === true) {
                toast.success(res.data.message || t("Sample collection updated successfully."));
                onUpdated?.(res.data.data);
                onHide();
            } else {
                toast.warning(res.data.message || t("Unable to update sample"));
            }
        } catch (error) {
            console.log(error);
            toast.warning(error?.response?.data?.message || t("Unable to update sample"));
        } finally {
            setLoading(false);
        }
    };

    const handleCollect = async () => {
        if (!detail?._id) return;

        setLoading(true);
        try {
            const payload = {
                status: "Collected",
                collectionTime: new Date().toISOString(),
            };
            const res = await updateSampleCollectionStatus(detail._id, payload);
            if (res.data.success === true) {
                toast.success(res.data.message || t("Sample marked as collected."));
                const updated = {
                    ...detail,
                    ...res.data.data,
                    status: "Collected",
                    collectionTime: payload.collectionTime,
                };
                setDetail(updated);
                onUpdated?.(updated);
            } else {
                toast.warning(res.data.message || t("Unable to update status"));
            }
        } catch (error) {
            console.log(error);
            toast.warning(error?.response?.data?.message || t("Unable to update status"));
        } finally {
            setLoading(false);
        }
    };

    if (!detail) {
        return (
            <Modal show={show} onHide={onHide} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t("Sample Details")}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center text-muted py-5">{t("Loading...")}</Modal.Body>
            </Modal>
        );
    }

    const title = isBarcodeMode
        ? t("Barcode Lookup")
        : editable
            ? t("Edit Sample Collection")
            : t("Sample Collection Details");

    return (
        <Modal show={show} onHide={onHide} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title>{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="row g-3">
                    <div className="col-md-6">
                        <div className="text-muted small">{t("Barcode")}</div>
                        <div className="fw-semibold">{detail.barcodeId || "—"}</div>
                    </div>
                    <div className="col-md-6">
                        <div className="text-muted small">{t("Status")}</div>
                        {editable ? (
                            <Form.Select
                                size="sm"
                                value={status}
                                onChange={(e) => setStatus(e.target.value)}
                            >
                                {!SAMPLE_COLLECTION_UPDATE_STATUSES.includes(status) && status ? (
                                    <option value={status}>{t(status)}</option>
                                ) : null}
                                {SAMPLE_COLLECTION_UPDATE_STATUSES.map((option) => (
                                    <option key={option} value={option}>
                                        {t(option)}
                                    </option>
                                ))}
                            </Form.Select>
                        ) : (
                            <div className="fw-semibold">{detail.status ? t(detail.status) : "—"}</div>
                        )}
                    </div>

                    <div className="col-md-6">
                        <div className="text-muted small">{t("Patient")}</div>
                        <div className="fw-semibold">{detail.patientName || "—"}</div>
                        <small className="text-muted">
                            {[detail.patientGender, detail.patientAge ? `${t("Age")} ${detail.patientAge}` : null]
                                .filter(Boolean)
                                .join(" · ") || "—"}
                        </small>
                    </div>
                    <div className="col-md-6">
                        <div className="text-muted small">{t("Contact")}</div>
                        <div className="fw-semibold">{detail.mobileNumber || "—"}</div>
                        <small className="text-muted">{detail.patientEmail || ""}</small>
                    </div>

                    <div className="col-md-6">
                        <div className="text-muted small">{t("Test")}</div>
                        <div className="fw-semibold">{detail.testName || "—"}</div>
                    </div>
                    <div className="col-md-6">
                        <div className="text-muted small">{t("Sample Type")}</div>
                        <div className="fw-semibold">{detail.sampleName || "—"}</div>
                        {detail.sampleDescription ? (
                            <small className="text-muted">{detail.sampleDescription}</small>
                        ) : null}
                    </div>

                    {isBarcodeMode ? (
                        <>
                            <div className="col-md-6">
                                <div className="text-muted small">{t("Doctor")}</div>
                                <div className="fw-semibold">{detail.doctorName || "—"}</div>
                            </div>
                            <div className="col-md-6">
                                <div className="text-muted small">{t("Order Status")}</div>
                                <div className="fw-semibold">{detail.orderStatus || "—"}</div>
                            </div>
                            <div className="col-md-6">
                                <div className="text-muted small">{t("Lab")}</div>
                                <div className="fw-semibold">{detail.labName || "—"}</div>
                            </div>
                            <div className="col-md-6">
                                <div className="text-muted small">{t("Order Date")}</div>
                                <div className="fw-semibold">{formatDateTime(detail.orderDate)}</div>
                            </div>
                        </>
                    ) : (
                        <div className="col-md-6">
                            <div className="text-muted small">{t("Collection Time")}</div>
                            <div className="fw-semibold">{formatDateTime(detail.collectionTime)}</div>
                        </div>
                    )}

                    <div className="col-12">
                        <Form.Label className="text-muted small mb-1">{t("Assigned Equipment")}</Form.Label>
                        {editable ? (
                            <Select
                                isMulti
                                isSearchable
                                closeMenuOnSelect={false}
                                hideSelectedOptions={false}
                                options={equipmentOptions}
                                value={selectedEquipment}
                                onChange={(selected) => setSelectedEquipment(selected || [])}
                                placeholder={t("Select equipment...")}
                                classNamePrefix="react-select"
                                styles={selectStyles}
                            />
                        ) : (
                            <div className="fw-semibold">
                                {(detail.assignedEquipment || [])
                                    .map((eq) => (typeof eq === "string" ? eq : eq?.name))
                                    .filter(Boolean)
                                    .join(", ") || "—"}
                            </div>
                        )}
                    </div>

                    <div className="col-12">
                        <Form.Label className="text-muted small mb-1">{t("Notes")}</Form.Label>
                        {editable ? (
                            <Form.Control
                                as="textarea"
                                rows={3}
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                placeholder={t("Enter notes")}
                            />
                        ) : (
                            <div className="fw-semibold">{detail.notes || "—"}</div>
                        )}
                    </div>
                </div>
            </Modal.Body>
            <Modal.Footer>
                {!editable && mode !== "barcode" ? (
                    <Button variant="outline-primary" onClick={() => setEditable(true)}>
                        {t("Edit")}
                    </Button>
                ) : null}
                {editable ? (
                    <Button variant="primary" onClick={handleSave}>
                        {t("Save")}
                    </Button>
                ) : null}
                {detail.status === "Pending" ? (
                    <Button variant="success" onClick={handleCollect}>
                        {t("Mark Collected")}
                    </Button>
                ) : null}
                <Button variant="secondary" onClick={onHide}>
                    {t("Close")}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
